import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { api } from '../api';
import { lerFragmento } from '../acesso/fragmento';
import { t } from '../i18n/idioma';
import { NOME_DO_JOGO } from '../marca';
import { SeletorDeIdioma } from '../components/SeletorDeIdioma';
import { CenaDeEntrada } from './entrada/CenaDeEntrada';

function semReferencia() {
  const m = document.createElement('meta');
  m.name = 'referrer';
  m.content = 'no-referrer';
  document.head.appendChild(m);
  return () => m.remove();
}

export function AcessoPublico() {
  const caminho = window.location.pathname.replace(/\/+$/, '') || '/';
  const frag = useMemo(() => lerFragmento(), []);
  useEffect(semReferencia, []);
  if (caminho === '/convite') return <ParaOConvite codigo={frag.c ?? ''} email={frag.e ?? ''} />;
  if (caminho.endsWith('/confirmar')) return <PaginaToken modo="confirmar" token={frag.t ?? ''} />;
  if (caminho.endsWith('/sair')) return <PaginaToken modo="sair" token={frag.t ?? ''} />;
  if (caminho.endsWith('/reenviar')) return <PaginaReenviar />;
  return <ParaOFormulario />;
}

/**
 * O link do e-mail do convite (`/convite#c=<código>`) abre o login da landing na aba Convite (techspec-landing-fase
 * §06.7): o código e o e-mail seguem no fragmento, que não vai para log nenhum.
 */
function ParaOConvite({ codigo, email }: { codigo: string; email: string }) {
  useEffect(() => {
    const pares = [['convite', '1'], ['c', codigo], ['e', email]].filter(([, v]) => v)
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
    window.location.replace(`/${window.location.search}#${pares}`);
  }, [codigo, email]);
  return null;
}

/** O formulário de pré-registro mora na landing (techspec-landing-fase §05.1). */
function ParaOFormulario() {
  useEffect(() => { window.location.replace(`/${window.location.search}#pre-registro`); }, []);
  return null;
}

function Moldura({ children }: { children: ReactNode }) {
  return (
    <div className="tela tela--rola">
      <CenaDeEntrada />
      <main className="centro">
        <section className="gump">
          <div className="gump__body">
            <div className="pilha">{children}</div>
          </div>
        </section>
      </main>
      <footer className="rodape">
        <span>{NOME_DO_JOGO}</span>
        <span className="rodape__dir"><SeletorDeIdioma /></span>
      </footer>
    </div>
  );
}

function PaginaToken({ modo, token }: { modo: 'confirmar' | 'sair'; token: string }) {
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    const run = modo === 'confirmar'
      ? api.confirmarAcesso(token).then(r => setMsg(r.fase ? t('acesso.confirmadoFase', { nome: r.fase }) : t('acesso.confirmado')))
      : api.sairDaLista(token).then(() => setMsg(t('acesso.saiu')));
    void run.catch(err => setMsg(err instanceof Error ? err.message : t('acesso.linkInvalido')));
  }, [modo, token]);
  return (
    <Moldura>
      <h1>{modo === 'confirmar' ? t('acesso.confirmar') : t('acesso.sair')}</h1>
      <p className="aviso aviso--status" role="status">{msg ?? t('entrada.verificando')}</p>
      <a className="btn btn--larga" href="/">{t('comum.voltar')}</a>
    </Moldura>
  );
}

function PaginaReenviar() {
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const enviar = (e: FormEvent) => {
    e.preventDefault();
    void api.reenviarConvite(email.trim())
      .then(r => setMsg(r.message))
      .catch(() => setMsg(t('acesso.enviado')));
  };
  return (
    <Moldura>
      <h1>{t('acesso.reenviar')}</h1>
      {msg ? <p className="aviso aviso--status" role="status">{msg}</p> : (
        <form className="pilha" onSubmit={enviar}>
          <label className="campo">
            <span className="campo__rotulo">{t('auth.email')}</span>
            <span className="campo__caixa">
              <input type="email" value={email} required onChange={e => setEmail(e.target.value)} />
            </span>
          </label>
          <button type="submit" className="btn btn--primary" disabled={!email.trim()}>{t('acesso.reenviar')}</button>
        </form>
      )}
    </Moldura>
  );
}
